import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { PrimeReactProvider } from '@primereact/core';
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import Lara from '@primeuix/themes/lara';
import Material from '@primeuix/themes/material';
import Nora from '@primeuix/themes/nora';

import {
    API_ACCOUNT_COLOR, API_ACCOUNT_COLOR_SHEET, API_COLORSHEET_DEFAULT, API_CONFIG_THEME, API_CONFIG_THEME_SHEET,
} from './EsConstants';
import { esDelete, esFetch, esPut, esQuery } from './EsFetch';
import { ES_TOKEN_NAMES, esAsHex, esBuiltIn, esToken, esTokenVar } from './EsThemeTokens';

/**
 * What the app looks like, and where that answer comes from.
 *
 * This is houndup's theme menu ported: the same three modes, the same
 * twenty-one colors, one panel in the nav bar. It is a port of BEHAVIOR and
 * not of code, because the mechanism houndup uses does not exist here.
 *
 *
 * WHY NONE OF HOUNDUP'S CODE COULD COME ACROSS
 *
 * hupfront swaps a prebuilt theme.css out of public/themes/lara-light-blue and
 * writes its own --primary-N / --surface-N variables onto <html> from
 * tailwindcss/colors. **PrimeReact 11 ships no per-theme stylesheet at all** --
 * there is no themes directory to swap out of. A theme is a PRESET: a plain
 * object of design tokens the provider resolves into --p-* CSS variables and
 * injects. So a color change here is a new preset, not a new <link>.
 *
 * houndup also drives its mode with next-themes, which is a Next package. There
 * is no Next here. The mode is the `dark` class on <html> that this app has
 * always toggled -- see the note on the class further down; this file takes over
 * the toggling that used to sit in js/themeNav.js, so there is still exactly one
 * way to go dark.
 *
 *
 * THE PRESET IS A PROP, NOT A GLOBAL UPDATE
 *
 * @primeuix/themes exports updatePrimaryPalette / updateSurfacePalette, which
 * look like the obvious runtime API and are the wrong tool here. Both were
 * probed rather than assumed (test/EsPrimeProbe9.test.jsx) and both disappoint:
 *
 *   - updatePrimaryPalette works, and then a PrimeReactProvider RERENDER
 *     re-applies the `theme` prop and puts the old palette straight back. Every
 *     unrelated state change above this component would silently undo a color.
 *
 *   - updateSurfacePalette does nothing at all under Lara. It writes into
 *     semantic.colorScheme.light/dark.surface, and Lara has no colorScheme key
 *     -- its surface is a flat ramp of light-dark() pairs -- so the update lands
 *     somewhere nothing reads.
 *
 * Handing PrimeReactProvider a freshly defined preset does work, for both
 * palettes, and survives rerenders by construction because the prop IS the
 * state. That is why this provider renders PrimeReactProvider itself rather
 * than sitting inside it.
 *
 *
 * WHAT THIS MENU DOES NOT OFFER, AND WHY
 *
 * houndup has a third color grid, Text, and six shade sliders. Neither is
 * expressible as a v11 preset palette, so neither is here rather than being
 * faked with a stylesheet:
 *
 *   - **Text.** Lara derives it -- semantic.text.color is
 *     light-dark({surface.700}, {surface.0}) -- so there is no text palette to
 *     point anywhere. In v11 the text color IS the surface color, further
 *     along the same ramp, which is what the Surface grid here already moves.
 *
 *   - **The shade sliders.** A shade is a token override rather than a palette,
 *     and with darkModeSelector false (see index.js) the preset emits one value
 *     per token, so there is only one mode's worth of ramp to slide anyway.
 *
 *
 * WHERE THE ANSWER COMES FROM, IN THE ORDER IT IS BELIEVED
 *
 *   the built-in default -- dark, indigo, slate -- which is what this app has
 *   rendered since the PrimeReact 11 upgrade, so an environment nobody has
 *   configured looks exactly as it does today;
 *
 *   the last answer esback gave this browser, from localStorage, so the first
 *   paint after a reload is the right color instead of the default;
 *
 *   what esback says now, for this environment. That is the real answer, and it
 *   overwrites the two above when it arrives.
 *
 * **localStorage is a CACHE of the server's answer and never a store of a local
 * pick.** Tim asked for the choice to be remembered for the environment the app
 * is running in, which is the opposite of per-browser: two people on dev see the
 * same colors, and a person on dev and prod sees two different ones. Writing a
 * pick here rather than to esback would rebuild the per-browser behavior that
 * was explicitly not wanted.
 */

/** Light, dark, or whatever the reader's machine is set to. Mirrors EsThemeMode on esback. */
export const ES_MODE_LIGHT = 'light';
export const ES_MODE_DARK = 'dark';
export const ES_MODE_SYSTEM = 'system';
export const ES_MODES = [ ES_MODE_LIGHT, ES_MODE_DARK, ES_MODE_SYSTEM ];

/**
 * The colors both grids offer.
 *
 * houndup's list exactly -- four neutrals and every Tailwind hue but `neutral`,
 * which the four greys already cover -- and, one for one, the primitives Lara
 * ships. That correspondence is what lets this be a real port instead of an
 * approximation, and it is asserted rather than assumed in EsPrimeProbe9.
 *
 * Lower case, because a design token is `{rose.500}` and an enum constant is
 * ROSE. Mirrors EsThemeColor on esback; change one, change both.
 */
export const ES_COLORS = [
    'slate', 'gray', 'zinc', 'stone',
    'red', 'orange', 'amber', 'yellow', 'lime', 'green', 'emerald',
    'teal', 'cyan', 'sky', 'blue', 'indigo', 'violet', 'purple',
    'fuchsia', 'pink', 'rose',
];

/**
 * What an environment nobody has configured looks like.
 *
 * These are today's values, not a redesign. `indigo` is what index.js pinned by
 * hand at the v11 upgrade; `slate` is what Lara's own surface resolves to under
 * a light color-scheme; `dark` is what index.html's `class="dark"` and
 * themeNav's old toggler between them produced on first paint.
 */
export const ES_DEFAULT_MODE = ES_MODE_DARK;
export const ES_DEFAULT_PRIMARY = 'indigo';
export const ES_DEFAULT_SURFACE = 'slate';

/**
 * The PrimeReact preset under the palettes: shapes, shadows, and each component's own
 * pairings. Lara is what the app has always been in and what an unchosen preset means;
 * the Prime Aura, Prime Lara, Prime Nora, and Prime Material sheets each set their own
 * (Tim, 2026-10-10: "if a prime colorsheet is chosen, change the underlying theme too").
 * Lower case here, PRESET names on esback (EsThemePreset); change one, change both.
 */
export const ES_PRESETS = [ 'lara', 'aura', 'nora', 'material' ];
export const ES_DEFAULT_PRESET = 'lara';
const ES_PRESET_BASE = { lara: Lara, aura: Aura, nora: Nora, material: Material };

const ES_STEPS = [ 50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950 ];

/** The class the whole app's dark: variant keys on. See EsCommon.css. */
const DARK_CLASS = 'dark';

/** Where the last answer esback gave is kept. A cache, not a preference. */
const CACHE_KEY = 'es-theme';

/** A name from somewhere untrusted, folded to the list or dropped. */
export const esAsColor = ( pValue ) => {
    const aName = String( pValue ?? '' ).toLowerCase();
    return ES_COLORS.includes( aName ) ? aName : null;
};

export const esAsMode = ( pValue ) => {
    const aName = String( pValue ?? '' ).toLowerCase();
    return ES_MODES.includes( aName ) ? aName : null;
};

export const esAsPreset = ( pValue ) => {
    const aName = String( pValue ?? '' ).toLowerCase();
    return ES_PRESETS.includes( aName ) ? aName : null;
};

/** What esback stores: ROSE, not rose. */
export const esAsStored = ( pValue ) => ( pValue ? String( pValue ).toUpperCase() : null );

/** The 500 of a color, which is what a swatch shows. Lara's own primitives. */
export const esSwatch = ( pColor ) => Lara.primitive?.[ pColor ]?.[ 500 ] ?? 'transparent';

/**
 * One color as the eleven design-token references a palette is made of.
 *
 * `{rose.500}` and not `#f43f5e`: a token reference resolves to
 * var(--p-rose-500), so the emitted variable still points at the primitive and
 * a page can be read in the browser's inspector as "primary is rose".
 */
export const esRamp = ( pColor ) => Object.fromEntries(
    ES_STEPS.map( ( aStep ) => [ aStep, '{' + pColor + '.' + aStep + '}' ] )
);

/**
 * PrimeReact's own color mode, which is NOT the app's. Moved here from
 * index.js with its reasoning intact, because the preset now lives here.
 *
 * es-0.4.0 imported lara-light-indigo: a light theme, fixed, whatever the app's
 * dark class was doing. That is where the white cards on the blue page come
 * from. Pointing darkModeSelector at .dark made PrimeReact follow the app
 * instead, and every card, panel and table came out near-black. false keeps the
 * preset light, which is what the design has always assumed -- and EsTheme.css
 * carries a paired light/dark rule for every part where that mattered.
 *
 * It is left at false by the theme menu too. Turning it on is a redesign of
 * every PrimeReact surface in the app, not a setting, and the pairs in
 * EsTheme.css would then be fighting the preset rather than completing it.
 *
 * cssLayer is the other half of that, and its default is the trap. Off, v11
 * emits its CSS unlayered, and an unlayered rule beats every layered one no
 * matter what the layer order says -- so PrimeReact won against all of
 * Tailwind, and the codex tree rendered as a white box with red labels while
 * its own bg-transparent and text-white sat there doing nothing. Named, and
 * ordered between base and components, PrimeReact still beats preflight and
 * now loses to the app's classes and utilities. The order string must match
 * the one every stylesheet declares, since the first mention fixes it.
 */
export const ES_THEME_OPTIONS = {
    darkModeSelector: false,
    cssLayer: {
        name: 'primereact',
        order: 'theme, base, primereact, components, utilities',
    },
};

/**
 * PrimeReact's overlay layers, lifted over the sticky nav bar.
 *
 * <p>PrimeReact stacks every portalled overlay (dialog and its mask, select, menu,
 * popover, tooltip) from the provider's `zIndex` config: modal 1100, overlay and menu
 * 1000, tooltip 1100, written inline on the element. The scrolled nav is `.stickyNav`,
 * fixed at z-[9999] (EsTheme.css), so every one of them opened UNDER the nav and a
 * dialog's close X was covered. The rule that used to sit on .p-dialog could never win:
 * an inline z-index beats any stylesheet.
 *
 * <p>The nav's layer is added to the defaults rather than replacing them, so the order
 * between the kinds stays PrimeReact's own (a select opened inside a dialog still beats
 * it). Keep NAV_LAYER equal to the z-index on .stickyNav.
 */
export const NAV_LAYER = 9999;
export const ES_Z_INDEX = {
    modal: NAV_LAYER + 1100,
    overlay: NAV_LAYER + 1000,
    menu: NAV_LAYER + 1000,
    tooltip: NAV_LAYER + 1100,
};

/**
 * The chosen preset with this environment's two palettes on it.
 *
 * Lara is the family the app has always been in, and the base when none is
 * chosen; a sheet may choose Aura, Nora or Material instead. What the palettes
 * change is the primary and surface ramps; everything else -- radii, spacing,
 * every component's own tokens -- is the base's. definePreset deep-merges, so surface keeps its 0 step
 * (#ffffff), which content.background and primary.contrastColor both reference
 * and which a bare 50-950 ramp would otherwise take out from under them.
 *
 * One component token is a repair, not a restyle, and Lara's alone: @primeuix/themes
 * 3.0.1 ships Lara's secondary button focus ring as `light-dark{surface.200}, ...)`,
 * missing its "(", so a browser drops the declaration and a secondary button
 * shows no focus ring at all. This is Lara's own value with the paren back. The
 * other three draw that ring as a color with no shadow, and keep their own.
 *
 * The other is the primary button's fill, one step darker, on every base. White on
 * step 500 is 4.46:1 for indigo, under WCAG AA's 4.5 (the 2026-10-03 sweep: 160
 * nodes, 34 views). light-dark() resolves to its light value in both modes here
 * (no color-scheme is set), so the fix is the same in both.
 */
export const esPreset = ( pPrimary, pSurface, pPreset = ES_DEFAULT_PRESET ) => {
    const aBase = ES_PRESET_BASE[ pPreset ] ?? Lara;
    const aSecondary = aBase === Lara ? {
        focusRing: {
            shadow: '0 0 0 0.2rem light-dark({surface.200}, color-mix(in srgb, {surface.300}, transparent 80%))',
        },
    } : {};
    return definePreset( aBase, {
        semantic: {
            primary: esRamp( pPrimary ),
            surface: esRamp( pSurface ),
        },
        components: {
            button: {
                root: {
                    primary: {
                        background: '{primary.600}',
                        hoverBackground: '{primary.700}',
                        activeBackground: '{primary.800}',
                        borderColor: '{primary.600}',
                        hoverBorderColor: '{primary.700}',
                        activeBorderColor: '{primary.800}',
                    },
                    secondary: aSecondary,
                },
            },
        },
    } );
};

// ---------------------------------------------------------------------------
// The cache, and the one fetch
// ---------------------------------------------------------------------------

const readCache = () => {
    try {
        const aRaw = window.localStorage.getItem( CACHE_KEY );
        return aRaw ? JSON.parse( aRaw ) : null;
    }
    catch {
        // Private browsing, a full store, or a value somebody edited by hand.
        // The app has a look without it.
        return null;
    }
};

const writeCache = ( pTheme ) => {
    try {
        window.localStorage.setItem( CACHE_KEY, JSON.stringify( pTheme ) );
    }
    catch {
        // Same as readCache: a color that does not paint one frame sooner is
        // not worth failing anything over.
    }
};

/**
 * The one GET, memoised for the life of the tab.
 *
 * Module level rather than a ref, because that is what actually satisfies "the
 * SPA should not fetch this twice per navigation": a ref is per instance, and a
 * provider that remounts -- StrictMode does exactly that in development -- would
 * ask again. Every caller after the first gets the same promise.
 *
 * There is no HTTP caching on the endpoint on purpose (see EsConfigController):
 * a max-age would mean the admin who just pressed a swatch reloads into a stale
 * copy of the page they changed.
 */
let themePromise = null;

export const esFetchTheme = () => {
    if ( !themePromise ) {
        themePromise = esFetch( API_CONFIG_THEME )
            .then( ( pResponse ) => ( pResponse.ok ? pResponse.json() : null ) )
            // esback unreachable, or answering something that is not JSON. The
            // app renders on its built-in defaults, which is the whole reason
            // they are stated in this file rather than left implicit in a
            // stylesheet.
            .catch( () => null );
    }
    return themePromise;
};

/**
 * The default colorsheet -- the sheet the site's colors are -- asked once per tab like the
 * theme, and again (pFresh) when this tab moved it. null when there is none: no store here.
 */
let defaultSheetPromise = null;

export const esFetchDefaultSheet = ( pFresh ) => {
    if ( !defaultSheetPromise || pFresh ) {
        defaultSheetPromise = esFetch( API_COLORSHEET_DEFAULT )
            .then( ( pResponse ) => ( pResponse.ok ? pResponse.json() : null ) )
            .then( ( pSheet ) => ( pSheet?.id ? pSheet : null ) )
            .catch( () => null );
    }
    return defaultSheetPromise;
};

/** Test seam: forget the memoised fetches. Nothing in src calls this. */
export const esResetThemeFetch = () => { themePromise = null; defaultSheetPromise = null; };

// ---------------------------------------------------------------------------
// The colors -- the second half of the theme, since the editor (2026-09-24)
// ---------------------------------------------------------------------------
//
// THREE LAYERS AND A PREVIEW, believed in this order: the built-in values in
// EsCommon.css; the site's defaults (es_config), which ARE the default colorsheet's
// colors; the DRAFT -- the signed-in account's own (es_account_color), an admin's
// (this browser), or, signed out, this tab's session; and on top, whatever the
// picker is dragging right now and has not saved. Each layer is
// { <token>: { light, dark } } of hex, and a missing entry means "the layer below".
// mode, primary and surface layer the same way.
//
// EDITING IS EDITING A COLORSHEET, for everyone: the sheet picked, or the default
// sheet while nothing is. The draft is the edit, and `dirty` says it differs from
// that sheet as saved. An admin's draft never reaches the site on its own -- not even
// a draft of the default sheet; saving the default sheet is what changes everyone's
// colors (EsColorsheetBar, EsColorsheetService.save).
//
// THE CSS IS ONE <style> IN <head>, and it holds overrides only -- a token
// nobody changed has no line, so its built-in value in EsCommon.css stands.
// Light goes on html:not(.dark), which every element inherits. Dark cannot go on
// <html> alone: EsCommon.css re-declares every dark token on EACH element under
// .dark (`:where(.dark, .dark *)`), and an inherited value loses to a value
// declared on the element itself -- so the override has to be declared on every
// element too, `html.dark, html.dark *`, where (0,1,1) beats the :where's zero.
// Unlayered, like the block it overrides, so no layer order can bury it.

export const ES_COLORS_STYLE_ID = 'es-theme-colors';

/** Layers merged top-down into one { token: { light, dark } }, hex only. */
export const esMergeColors = ( ...pLayers ) => {
    const aOut = {};
    pLayers.forEach( ( pLayer ) => Object.entries( pLayer ?? {} ).forEach( ( [ aToken, aModes ] ) => {
        if ( !esToken( aToken ) ) return;
        [ 'light', 'dark' ].forEach( ( aMode ) => {
            const aHex = esAsHex( aModes?.[ aMode ] );
            if ( aHex ) aOut[ aToken ] = { ...aOut[ aToken ], [ aMode ]: aHex };
        } );
    } ) );
    return aOut;
};

/** The stylesheet for a merged set of overrides. Empty when there are none. */
export const esColorsCss = ( pColors ) => {
    const aLines = ( pMode ) => Object.entries( pColors ?? {} )
        .filter( ( [ , aModes ] ) => aModes?.[ pMode ] )
        .map( ( [ aToken, aModes ] ) => '    ' + esTokenVar( aToken ) + ': ' + aModes[ pMode ] + ';' );

    const aLight = aLines( 'light' );
    const aDark = aLines( 'dark' );
    return ( aLight.length ? 'html:not(.dark) {\n' + aLight.join( '\n' ) + '\n}\n' : '' )
        + ( aDark.length ? 'html.dark, html.dark * {\n' + aDark.join( '\n' ) + '\n}\n' : '' );
};

const writeStyle = ( pCss ) => {
    let aNode = document.getElementById( ES_COLORS_STYLE_ID );
    if ( !pCss ) {
        aNode?.remove();
        return;
    }
    if ( !aNode ) {
        aNode = document.createElement( 'style' );
        aNode.id = ES_COLORS_STYLE_ID;
        document.head.appendChild( aNode );
    }
    if ( aNode.textContent !== pCss ) aNode.textContent = pCss;
};

/** A server answer as a layer: names folded, colors hex, junk dropped. */
const asLayer = ( pTheme ) => ( {
    mode: esAsMode( pTheme?.mode ),
    primary: esAsColor( pTheme?.primary ),
    surface: esAsColor( pTheme?.surface ),
    preset: esAsPreset( pTheme?.preset ),
    colors: esMergeColors( pTheme?.colors ),
} );

const EMPTY = { mode: null, primary: null, surface: null, preset: null, colors: {} };

/** The four PrimeReact settings a layer may hold beside its cells. */
const SETTINGS = [ 'mode', 'primary', 'surface', 'preset' ];

/** Choices a layer holds: each set setting, and each light or dark cell. */
export const esCountChoices = ( pLayer ) =>
    SETTINGS.filter( ( aKey ) => pLayer?.[ aKey ] ).length
    + Object.values( pLayer?.colors ?? {} )
        .reduce( ( aSum, aModes ) => aSum + [ 'light', 'dark' ].filter( ( aMode ) => aModes?.[ aMode ] ).length, 0 );

const setCell = ( pColors, pToken, pMode, pHex ) => {
    const aModes = { ...pColors?.[ pToken ] };
    if ( pHex ) aModes[ pMode ] = pHex;
    else delete aModes[ pMode ];
    const aNext = { ...pColors };
    if ( Object.keys( aModes ).length ) aNext[ pToken ] = aModes;
    else delete aNext[ pToken ];
    return aNext;
};

/** The signed-out reader's picks, which last the browser session and no longer. */
const SESSION_KEY = 'es-theme-session';
/** The last answer GET /accountColor gave, for the first paint. A cache. */
const ACCOUNT_CACHE_KEY = 'es-theme-account';
/**
 * The colorsheet being edited, as saved -- { id, name, ownerId, colors, prime } -- which
 * the draft is measured against, and which the save icon asks before deciding between
 * saving over it and asking for a name. Absent means the default sheet. The browser's to
 * remember: sessionStorage signed out, localStorage signed in. Signed in it carries the
 * account that kept it, `by`, and is another account's to nobody: a session that ends
 * without a logout leaves it behind for whoever signs in next. A value from before it held
 * the colors, or its account, is dropped, and the default sheet stands in.
 */
const SHEET_KEY = 'es-theme-sheet';
/**
 * An admin's draft: the sheet they are editing, over the site's defaults, held in this
 * browser until they save it, pick another, or reset. An admin has no account layer, and
 * their edits never write the site directly.
 */
const ADMIN_SHEET_KEY = 'es-theme-admin-sheet';

const readJson = ( pStore, pKey ) => {
    try {
        const aRaw = window[ pStore ].getItem( pKey );
        return aRaw ? JSON.parse( aRaw ) : null;
    }
    catch {
        return null;
    }
};

const readSheet = ( pStore, pAccountId ) => {
    const aSheet = readJson( pStore, SHEET_KEY );
    if ( pStore === 'localStorage' && ( pAccountId == null || aSheet?.by !== pAccountId ) ) return null;
    return aSheet?.id && aSheet.colors ? aSheet : null;
};

/** A saved sheet as a layer. */
const sheetLayer = ( pSheet ) => asLayer( { ...pSheet?.prime, colors: pSheet?.colors } );

/** What a layer over the site resolves to: every setting, and the cells stated anywhere. */
const resolveLook = ( pSite, pLayer ) => ( {
    mode: pLayer?.mode ?? pSite.mode ?? ES_DEFAULT_MODE,
    primary: pLayer?.primary ?? pSite.primary ?? ES_DEFAULT_PRIMARY,
    surface: pLayer?.surface ?? pSite.surface ?? ES_DEFAULT_SURFACE,
    preset: pLayer?.preset ?? pSite.preset ?? ES_DEFAULT_PRESET,
    colors: esMergeColors( pSite.colors, pLayer?.colors ),
} );

/** Two resolved looks the same to the eye: every setting, and every cell through the built-in. */
export const esSameLook = ( pA, pB ) => {
    if ( SETTINGS.some( ( aKey ) => pA[ aKey ] !== pB[ aKey ] ) ) return false;
    const aCell = ( pColors, pToken, pMode ) => pColors?.[ pToken ]?.[ pMode ] ?? esBuiltIn( pToken, pMode );
    return ES_TOKEN_NAMES.every( ( aToken ) => [ 'light', 'dark' ].every(
        ( aMode ) => aCell( pA.colors, aToken, aMode ) === aCell( pB.colors, aToken, aMode ) ) );
};

const readStore = ( pStore, pKey ) => {
    try {
        const aRaw = window[ pStore ].getItem( pKey );
        return aRaw ? asLayer( JSON.parse( aRaw ) ) : null;
    }
    catch {
        return null;
    }
};

const writeStore = ( pStore, pKey, pLayer ) => {
    try {
        if ( pLayer ) window[ pStore ].setItem( pKey, JSON.stringify( pLayer ) );
        else window[ pStore ].removeItem( pKey );
    }
    catch {
        // A pick that is already on screen is not worth failing over.
    }
};

// ---------------------------------------------------------------------------
// The provider
// ---------------------------------------------------------------------------

const EsThemeContext = createContext( null );

export function useEsTheme() {
    const aContext = useContext( EsThemeContext );
    if ( !aContext ) throw new Error( 'useEsTheme must be used inside EsThemeProvider' );
    return aContext;
}

export function EsThemeProvider( { children, license } ) {
    const aCached = useMemo( readCache, [] );

    // What esback says for this environment, seeded from the last answer.
    const [ site, setSite ] = useState( () => ( aCached ? asLayer( aCached ) : EMPTY ) );

    // The signed-in account's own, or null when nobody is, or when an admin is: an
    // admin's draft is `mine`. Told by EsThemeAccount, which sits inside AuthProvider --
    // this provider is above it and cannot ask.
    const [ who, setWho ] = useState( { signedIn: false, admin: false, accountId: null } );
    const [ account, setAccount ] = useState( null );

    // A signed-out reader's picks, which last the browser session.
    const [ session, setSession ] = useState( () => readStore( 'sessionStorage', SESSION_KEY ) ?? EMPTY );

    // What a picker is dragging and has not saved.
    const [ preview, setPreview ] = useState( {} );

    // The colorsheet being edited (null: the default one), the default colorsheet, and an
    // admin's draft (ADMIN_SHEET_KEY).
    const [ sheet, setSheet ] = useState( () => readSheet( 'sessionStorage' ) );
    const [ defaultSheet, setDefaultSheet ] = useState( null );
    const [ mine, setMine ] = useState( () => readStore( 'localStorage', ADMIN_SHEET_KEY ) );

    // What `system` is resolving to right now. Only ever light or dark.
    const [ systemDark, setSystemDark ] = useState( () => {
        // jsdom has no matchMedia, and neither does a very old browser. Neither
        // is a reason to fail to draw a page.
        try {
            return window.matchMedia( '(prefers-color-scheme: dark)' ).matches;
        }
        catch {
            return true;
        }
    } );

    // Whether this tab has moved the site's layer -- an admin's Make default, or a save
    // of the default sheet. esback's first answer arrives after the first paint, and
    // without this it could land on top of that and undo it.
    const touched = useRef( false );

    const own = who.admin ? mine : null;
    const pickOf = ( pKey, pDefault ) => own?.[ pKey ] ?? session[ pKey ] ?? account?.[ pKey ] ?? site[ pKey ] ?? pDefault;
    const mode = pickOf( 'mode', ES_DEFAULT_MODE );
    const primary = pickOf( 'primary', ES_DEFAULT_PRIMARY );
    const surface = pickOf( 'surface', ES_DEFAULT_SURFACE );
    const preset = pickOf( 'preset', ES_DEFAULT_PRESET );

    const resolved = mode === ES_MODE_SYSTEM
        ? ( systemDark ? ES_MODE_DARK : ES_MODE_LIGHT )
        : mode;

    // ---- what the mode does ------------------------------------------------
    //
    // The class on <html>, which is the mechanism this app already had.
    // EsCommon.css declares `@custom-variant dark (&:where(.dark, .dark *))`,
    // so every dark: utility in every view keys on it, and EsTheme.css styles
    // the portalled PrimeReact overlays through it -- they render under <body>,
    // outside the React tree, and are still descendants of <html>.
    //
    // index.html ships class="dark", so a dark environment paints correctly from
    // the very first frame and only a LIGHT one flashes dark for the frame
    // before this effect runs. The cache above shortens that to the first ever
    // visit. Known, and deliberately not fixed with an inline script in
    // index.html, which is a second place for the default to be stated and to
    // drift.
    useEffect( () => {
        document.documentElement.classList.toggle( DARK_CLASS, resolved === ES_MODE_DARK );
    }, [ resolved ] );

    // Follow the machine while, and only while, the answer says `system`.
    useEffect( () => {
        if ( mode !== ES_MODE_SYSTEM ) return undefined;

        let aQuery;
        try {
            aQuery = window.matchMedia( '(prefers-color-scheme: dark)' );
        }
        catch {
            return undefined;
        }

        const aListen = ( pEvent ) => setSystemDark( pEvent.matches );
        setSystemDark( aQuery.matches );
        aQuery.addEventListener( 'change', aListen );
        return () => aQuery.removeEventListener( 'change', aListen );
    }, [ mode ] );

    // ---- what esback says for the site -------------------------------------

    useEffect( () => {
        let aStale = false;

        esFetchTheme().then( ( pTheme ) => {
            if ( aStale || !pTheme ) return;

            const aNext = asLayer( pTheme );

            // Cached even when every field is null. "This environment has
            // chosen nothing" is an answer worth remembering, and not caching it
            // would leave a stale earlier answer in place for ever.
            writeCache( aNext );

            if ( touched.current ) return;
            setSite( aNext );
        } );

        return () => { aStale = true; };
    }, [] );

    useEffect( () => {
        let aStale = false;
        esFetchDefaultSheet().then( ( pSheet ) => { if ( !aStale ) setDefaultSheet( pSheet ); } );
        return () => { aStale = true; };
    }, [] );

    // ---- and for the account ------------------------------------------------

    useEffect( () => {
        if ( !who.signedIn || who.admin ) {
            setAccount( null );
            writeStore( 'localStorage', ACCOUNT_CACHE_KEY, null );
            return undefined;
        }

        setAccount( ( pWas ) => pWas ?? readStore( 'localStorage', ACCOUNT_CACHE_KEY ) ?? EMPTY );

        // Signing in ends the signed-out session's picks: from here the
        // account's own colors are what this person sees.
        setSession( EMPTY );
        writeStore( 'sessionStorage', SESSION_KEY, null );

        let aStale = false;
        esFetch( API_ACCOUNT_COLOR )
            .then( ( pResponse ) => ( pResponse.ok ? pResponse.json() : null ) )
            .then( ( pTheme ) => {
                if ( aStale || !pTheme ) return;
                const aNext = asLayer( pTheme );
                writeStore( 'localStorage', ACCOUNT_CACHE_KEY, aNext );
                setAccount( aNext );
            } )
            .catch( () => undefined );

        return () => { aStale = true; };
    }, [ who.signedIn, who.admin ] );

    // Signing in or out changes which store remembers the sheet on screen, and whose.
    useEffect( () => {
        setSheet( who.signedIn ? readSheet( 'localStorage', who.accountId ) : readSheet( 'sessionStorage' ) );
    }, [ who.signedIn, who.accountId ] );

    // ---- the stylesheet ------------------------------------------------------

    const layerColors = who.signedIn ? ( who.admin ? own?.colors : account?.colors ) : session.colors;
    const colors = useMemo( () => esMergeColors( site.colors, layerColors, preview ),
        [ site.colors, layerColors, preview ] );

    useEffect( () => { writeStyle( esColorsCss( colors ) ); }, [ colors ] );

    // ---- writing -------------------------------------------------------------

    /** Where an edit made now would land: 'sheet' (an admin's draft), 'account' or 'session'. */
    const aim = who.signedIn ? ( who.admin ? 'sheet' : 'account' ) : 'session';

    const keepSession = ( pNext ) => {
        setSession( pNext );
        writeStore( 'sessionStorage', SESSION_KEY, pNext );
    };

    const keepMine = ( pNext ) => {
        setMine( pNext );
        writeStore( 'localStorage', ADMIN_SHEET_KEY, pNext );
    };

    const keepSheet = useCallback( ( pNext ) => {
        const aNext = pNext
            ? { id: pNext.id, name: pNext.name, ownerId: pNext.ownerId ?? null, colors: pNext.colors ?? {},
                prime: pNext.prime ?? null }
            : null;
        setSheet( aNext );
        [ 'sessionStorage', 'localStorage' ].forEach( ( aStore ) => {
            try {
                if ( aNext && ( aStore === 'localStorage' ) === who.signedIn ) {
                    window[ aStore ].setItem( SHEET_KEY, JSON.stringify( who.signedIn ? { ...aNext, by: who.accountId } : aNext ) );
                }
                else window[ aStore ].removeItem( SHEET_KEY );
            }
            catch {
                // Which sheet is on screen is a convenience, not worth failing over.
            }
        } );
    }, [ who.signedIn, who.accountId ] );

    /**
     * Settings and cells, into whichever layer is aimed at, as ONE change to it: the
     * session and an admin's draft are replaced whole, so two writes in one tick would
     * each start from the same stale layer and the second would drop the first.
     * pPatch holds mode, primary or surface; pCells is [ { token, mode, hex } ]. A null
     * takes that setting or cell back to the layer below. An account's writes go to
     * esback one row each.
     */
    const editDraft = useCallback( ( pPatch, pCells ) => {
        const aKeys = Object.keys( pPatch ?? {} ).filter( ( aKey ) => SETTINGS.includes( aKey ) );
        const aFold = ( aKey ) => ( aKey === 'mode' ? esAsMode( pPatch[ aKey ] )
            : aKey === 'preset' ? esAsPreset( pPatch[ aKey ] ) : esAsColor( pPatch[ aKey ] ) );
        const aPatch = Object.fromEntries( aKeys.map( ( aKey ) => [ aKey, aFold( aKey ) ] ) );

        const aCells = ( pCells ?? [] )
            .filter( ( aCell ) => esToken( aCell.token ) && ( aCell.hex == null || esAsHex( aCell.hex ) ) )
            .map( ( aCell ) => ( { ...aCell, hex: aCell.hex == null ? null : esAsHex( aCell.hex ) } ) );
        if ( !aKeys.length && !aCells.length ) return;

        const aApply = ( pLayer ) => ( {
            ...( pLayer ?? EMPTY ), ...aPatch,
            colors: aCells.reduce( ( aColors, aCell ) => setCell( aColors, aCell.token, aCell.mode, aCell.hex ),
                pLayer?.colors ),
        } );

        if ( aCells.length ) {
            setPreview( ( pWas ) => aCells.reduce( ( aWas, aCell ) => setCell( aWas, aCell.token, aCell.mode, null ), pWas ) );
        }

        if ( aim === 'session' ) {
            keepSession( aApply( session ) );
            return;
        }

        if ( aim === 'sheet' ) {
            keepMine( aApply( mine ) );
            return;
        }

        setAccount( ( pWas ) => {
            const aAccount = aApply( pWas );
            writeStore( 'localStorage', ACCOUNT_CACHE_KEY, aAccount );
            return aAccount;
        } );
        const aSend = ( pToken, pMode, pValue ) => ( pValue
            ? esPut( API_ACCOUNT_COLOR, { token: pToken, mode: pMode, value: pValue } )
            : esDelete( API_ACCOUNT_COLOR + esQuery( { token: pToken, mode: pMode } ) ) )
            .catch( () => undefined );
        aKeys.forEach( ( aKey ) => aSend( 'theme.' + aKey, 'ANY', esAsStored( aPatch[ aKey ] ) ) );
        aCells.forEach( ( aCell ) => aSend( aCell.token, aCell.mode.toUpperCase(), aCell.hex ) );
    }, [ aim, session, mine ] );

    /** Mode, accent family or surface family. See editDraft. */
    const editTheme = useCallback( ( pPatch ) => editDraft( pPatch, null ), [ editDraft ] );

    /** Paint a value without saving it -- the picker mid-drag. null clears it. */
    const previewColor = useCallback( ( pToken, pMode, pHex ) => {
        setPreview( ( pWas ) => setCell( pWas, pToken, pMode, esAsHex( pHex ) ) );
    }, [] );

    /** One cell. null takes it back to the layer below: the site's, which is the default sheet's. */
    const setColor = useCallback( ( pToken, pMode, pHex ) => editDraft( null, [ { token: pToken, mode: pMode, hex: pHex } ] ),
        [ editDraft ] );

    /** The draft: the layer an edit lands in, whose own cells the editor marks as set. */
    const aimed = { sheet: mine ?? EMPTY, account: account ?? EMPTY, session }[ aim ];

    /** How many choices the draft holds -- mode and the two families count, as each is a row. */
    const resetCount = esCountChoices( aimed );

    // The draft as it looks, the sheet being edited as saved, and the default sheet --
    // which is the site's layer itself, so it needs nothing fetched to be compared.
    const drafted = useMemo( () => resolveLook( site, aimed ), [ site, aimed ] );
    const dirty = useMemo( () => !esSameLook( drafted, resolveLook( site, sheet ? sheetLayer( sheet ) : EMPTY ) ),
        [ drafted, site, sheet ] );
    const differsFromDefault = useMemo( () => !esSameLook( drafted, resolveLook( site, EMPTY ) ), [ drafted, site ] );
    /** Nothing to reset: the default sheet, unchanged. */
    const atDefault = !differsFromDefault && ( !sheet || sheet.id === defaultSheet?.id );

    const dropAccount = async () => {
        await esDelete( API_ACCOUNT_COLOR );
        setAccount( EMPTY );
        writeStore( 'localStorage', ACCOUNT_CACHE_KEY, EMPTY );
    };

    /**
     * Reset colors: back to the default colorsheet, whichever it is now. The draft goes,
     * the picked sheet goes, and an account's own colors go -- so the view is the site's.
     * Resolves once esback has answered; rejects when it refused.
     */
    const resetColors = useCallback( async () => {
        setPreview( {} );
        keepSheet( null );

        if ( aim === 'session' ) {
            keepSession( EMPTY );
            return;
        }

        if ( aim === 'sheet' ) {
            keepMine( null );
            return;
        }

        await dropAccount();
    }, [ aim, keepSheet ] );

    /**
     * The look on screen, for a save: every layer but the unsaved drag, and the three
     * settings as they resolve. A sheet is a whole look, so the site's cells are in it.
     */
    const look = useCallback( () => ( {
        colors: esMergeColors( site.colors, layerColors ),
        prime: { mode: esAsStored( mode ), primary: esAsStored( primary ), surface: esAsStored( surface ),
            preset: esAsStored( preset ) },
    } ), [ site.colors, layerColors, mode, primary, surface, preset ] );

    /**
     * A colorsheet picked, to edit and to see. Signed out it is this session's draft; for
     * an account it replaces the account's own colors on esback; for an admin it is their
     * draft, in this browser. The default sheet is the site's own layer, so picking it
     * empties the draft rather than copying the site into it -- it then follows the site.
     * Resolves once the look is on screen; rejects when esback refused.
     */
    const pickSheet = useCallback( async ( pSheet, pIsDefault ) => {
        setPreview( {} );
        const aLayer = pIsDefault ? EMPTY : sheetLayer( pSheet );

        if ( aim === 'session' ) {
            keepSession( aLayer );
        }
        else if ( who.admin ) {
            keepMine( pIsDefault ? null : aLayer );
        }
        else if ( pIsDefault ) {
            await dropAccount();
        }
        else {
            const aReset = await esPut( API_ACCOUNT_COLOR_SHEET + '/' + pSheet.id );
            const aAccount = asLayer( aReset?.theme );
            setAccount( aAccount );
            writeStore( 'localStorage', ACCOUNT_CACHE_KEY, aAccount );
        }

        keepSheet( pIsDefault ? null : pSheet );
    }, [ aim, who.admin, keepSheet ] );

    /**
     * A sheet just saved is the one being edited, and the draft now matches it. Saving the
     * default sheet changed the site's defaults on esback, so the site's layer follows and
     * an admin's draft of it is spent.
     */
    const sheetSaved = useCallback( ( pSheet, pIsDefault ) => {
        if ( !pIsDefault ) {
            keepSheet( pSheet );
            return;
        }
        touched.current = true;
        const aSite = sheetLayer( pSheet );
        setSite( aSite );
        writeCache( aSite );
        setDefaultSheet( pSheet );
        keepMine( null );
        keepSheet( null );
    }, [ keepSheet ] );

    /**
     * The saved copy of the sheet being edited, as esback has it now -- or null when it is
     * gone or has become the default, which the default then stands in for. The draft is
     * not touched.
     */
    const sheetLoaded = useCallback( ( pSheet ) => {
        if ( !sheet ) return;
        if ( !pSheet ) keepSheet( null );
        else if ( pSheet.id === sheet.id ) keepSheet( pSheet );
    }, [ sheet, keepSheet ] );

    /** The default sheet asked for again, when the list says another one is it. */
    const refreshDefault = useCallback( () => esFetchDefaultSheet( true ).then( setDefaultSheet ), [] );

    /** Make default: the site's defaults become the sheet, for everyone without their own. */
    const makeDefault = useCallback( async ( pSheetId ) => {
        const aTheme = await esPut( API_CONFIG_THEME_SHEET + '/' + pSheetId );
        touched.current = true;
        const aSite = asLayer( aTheme );
        setSite( aSite );
        writeCache( aSite );
        keepMine( null );
        keepSheet( null );
        setPreview( {} );
        await refreshDefault();
    }, [ keepSheet, refreshDefault ] );

    // Recomputed only when a color moves. Every render of this provider
    // otherwise hands PrimeReactProvider the same object, and a new preset
    // object means a full re-resolve and re-injection of the theme's CSS.
    const primePreset = useMemo( () => esPreset( primary, surface, preset ), [ primary, surface, preset ] );

    const value = useMemo( () => ( {
        mode, primary, surface, preset, resolved,
        colors, aim, aimed, site, admin: who.admin, resetCount,
        sheet, defaultSheet, dirty, differsFromDefault, atDefault,
        editDraft, editTheme, setColor, previewColor, resetColors, setWho,
        look, pickSheet, sheetSaved, sheetLoaded, refreshDefault, makeDefault,
    } ), [ mode, primary, surface, preset, resolved, colors, aim, aimed, site,
        who.admin, resetCount, sheet, defaultSheet, dirty, differsFromDefault, atDefault,
        editDraft, editTheme, setColor, previewColor, resetColors,
        look, pickSheet, sheetSaved, sheetLoaded, refreshDefault, makeDefault ] );

    return (
        <PrimeReactProvider theme={ { preset: primePreset, options: ES_THEME_OPTIONS } } zIndex={ ES_Z_INDEX } license={ license }>
            <EsThemeContext.Provider value={ value }>
                { children }
            </EsThemeContext.Provider>
        </PrimeReactProvider>
    );
}

/**
 * Tells EsThemeProvider who is signed in. It has to be a child of AuthProvider,
 * which is below EsThemeProvider (index.js says why), so the provider cannot ask
 * for itself. Renders nothing.
 *
 * The account's own colors are asked for only once somebody is signed in, and
 * never for an admin: a GET /accountColor from a logged-out visitor is a 401 in
 * the log per page load.
 */
export function EsThemeAccount( { auth } ) {
    const { setWho } = useEsTheme();
    const aSignedIn = Boolean( auth?.user );
    const aAdmin = Boolean( auth?.admin );
    const aAccountId = auth?.account?.accountId ?? null;

    useEffect( () => {
        setWho( { signedIn: aSignedIn, admin: aAdmin, accountId: aAccountId } );
    }, [ aSignedIn, aAdmin, aAccountId, setWho ] );

    return null;
}
